import { OnboardingStep } from '@nxsys/constants/AppEnums';

// Nationalities with an unrestricted right to work in the UK, so onboarding asks
// them for neither an ID check nor a share code. Northern Ireland is part of the
// United Kingdom, so Northern Irish workers are British citizens; Irish citizens
// hold the same unrestricted right under the Common Travel Area.
//
// This drives which step template a worker is shown, so it must stay in step
// with UK_RIGHT_TO_WORK_NATIONALITIES in the backend. If the backend routes a
// worker to the ID step while this list sends them to the share-code template,
// the step never renders and they cannot finish onboarding.
const UK_RIGHT_TO_WORK_NATIONALITIES = [
  'English',
  'Welsh',
  'British',
  'Scottish',
  'Northern Irish',
  'Irish',
];

const getOnboardingStepAndNationalities = ({
  isBureau,
  isIreland,
}: {
  isBureau: boolean;
  isIreland: boolean;
}): {
  nationalities: string[];
  stepsTemp: { [key in OnboardingStep]?: { label: string; key: string } };
  stepsTemp1: { [key in OnboardingStep]?: { label: string; key: string } };
  stepsTemp2: { [key in OnboardingStep]?: { label: string; key: string } };
} => {
  // Umbrella
  // NX-5228: country first. `!isBureau` alone returned the UK template for
  // every umbrella worker, so an Irish one was asked for P45 details, a P45
  // starter declaration, an NI Number and a UTR, got the UK right-to-work
  // nationality list, and never reached the Ireland branch below.
  if (!isBureau && !isIreland) {
    return {
      nationalities: UK_RIGHT_TO_WORK_NATIONALITIES,
      stepsTemp: {
        [OnboardingStep.ADD_NATIONALITY]: {
          label: 'Add Nationality',
          key: 'addNationality',
        },
        [OnboardingStep.ADD_ID]: {
          label: 'Verify your ID with passport',
          key: 'addID',
        },
        [OnboardingStep.ADD_SHARE_CODE]: {
          label: 'Add Share Code / EC Check Code',
          key: 'addShareCode',
        },
        [OnboardingStep.ADD_DOB]: { label: 'Add DOB', key: 'addDOB' },
        [OnboardingStep.ADD_BANK_DETAILS]: {
          label: 'Add Bank Details',
          key: 'addBankDetails',
        },
        [OnboardingStep.ADD_ADDRESS_DETAILS]: {
          label: 'Add Address Details',
          key: 'addAddressDetails',
        },
        [OnboardingStep.ADD_JOB_DESCRIPTION]: {
          label: 'Add Job Title',
          key: 'addJobDescription',
        },
        [OnboardingStep.ADD_WORKING_TIME_REGULATION]: {
          label: 'Add Working Time Regulation',
          key: 'addWorkingTimeRegulation',
        },
        [OnboardingStep.ADD_AGENCY_CONDUCT_REGULATION]: {
          label: 'Add Agency Conduct Regulation',
          key: 'addAgencyConductRegulation',
        },
        [OnboardingStep.ADD_P45_DETAILS]: {
          label: 'Add P45 Details',
          key: 'addP45Details',
        },
        [OnboardingStep.ADD_P45_STARTER_DECLARATION]: {
          label: 'Add P45 Starter Declaration',
          key: 'addP45StarterDeclaration',
        },
        [OnboardingStep.ADD_NI_NUMBER]: {
          label: 'Add NI Number',
          key: 'addNINumber',
        },
        [OnboardingStep.ADD_HOLIDAY_PAY_METHOD]: {
          label: 'Add Holiday Pay Method',
          key: 'addHolidayPayMethod',
        },
        [OnboardingStep.ADD_UTR]: { label: 'Add UTR Number', key: 'addUTR' },
        [OnboardingStep.ADD_SIGN_CONTRACT]: {
          label: 'Verify Contract',
          key: 'signContract',
        },
      },
      stepsTemp1: {
        [OnboardingStep.ADD_NATIONALITY]: {
          label: 'Add Nationality',
          key: 'addNationality',
        },
        [OnboardingStep.ADD_SHARE_CODE]: {
          label: 'Add Share Code / EC Check Code',
          key: 'addShareCode',
        },
        [OnboardingStep.ADD_DOB]: { label: 'Add DOB', key: 'addDOB' },
        [OnboardingStep.ADD_BANK_DETAILS]: {
          label: 'Add Bank Details',
          key: 'addBankDetails',
        },
        [OnboardingStep.ADD_ADDRESS_DETAILS]: {
          label: 'Add Address Details',
          key: 'addAddressDetails',
        },
        [OnboardingStep.ADD_JOB_DESCRIPTION]: {
          label: 'Add Job Title',
          key: 'addJobDescription',
        },
        [OnboardingStep.ADD_WORKING_TIME_REGULATION]: {
          label: 'Add Working Time Regulation',
          key: 'addWorkingTimeRegulation',
        },
        [OnboardingStep.ADD_AGENCY_CONDUCT_REGULATION]: {
          label: 'Add Agency Conduct Regulation',
          key: 'addAgencyConductRegulation',
        },
        [OnboardingStep.ADD_P45_DETAILS]: {
          label: 'Add P45 Details',
          key: 'addP45Details',
        },
        [OnboardingStep.ADD_P45_STARTER_DECLARATION]: {
          label: 'Add P45 Starter Declaration',
          key: 'addP45StarterDeclaration',
        },
        [OnboardingStep.ADD_NI_NUMBER]: {
          label: 'Add NI Number',
          key: 'addNINumber',
        },
        [OnboardingStep.ADD_HOLIDAY_PAY_METHOD]: {
          label: 'Add Holiday Pay Method',
          key: 'addHolidayPayMethod',
        },
        [OnboardingStep.ADD_UTR]: { label: 'Add UTR Number', key: 'addUTR' },
        [OnboardingStep.ADD_SIGN_CONTRACT]: {
          label: 'Verify Contract',
          key: 'signContract',
        },
      },
      stepsTemp2: {
        [OnboardingStep.ADD_NATIONALITY]: {
          label: 'Add Nationality',
          key: 'addNationality',
        },
        [OnboardingStep.ADD_ID]: {
          label: 'Verify your ID with passport',
          key: 'addID',
        },
        [OnboardingStep.ADD_DOB]: { label: 'Add DOB', key: 'addDOB' },
        [OnboardingStep.ADD_BANK_DETAILS]: {
          label: 'Add Bank Details',
          key: 'addBankDetails',
        },
        [OnboardingStep.ADD_ADDRESS_DETAILS]: {
          label: 'Add Address Details',
          key: 'addAddressDetails',
        },
        [OnboardingStep.ADD_JOB_DESCRIPTION]: {
          label: 'Add Job Title',
          key: 'addJobDescription',
        },
        [OnboardingStep.ADD_WORKING_TIME_REGULATION]: {
          label: 'Add Working Time Regulation',
          key: 'addWorkingTimeRegulation',
        },
        [OnboardingStep.ADD_AGENCY_CONDUCT_REGULATION]: {
          label: 'Add Agency Conduct Regulation',
          key: 'addAgencyConductRegulation',
        },
        [OnboardingStep.ADD_P45_DETAILS]: {
          label: 'Add P45 Details',
          key: 'addP45Details',
        },
        [OnboardingStep.ADD_P45_STARTER_DECLARATION]: {
          label: 'Add P45 Starter Declaration',
          key: 'addP45StarterDeclaration',
        },
        [OnboardingStep.ADD_NI_NUMBER]: {
          label: 'Add NI Number',
          key: 'addNINumber',
        },
        [OnboardingStep.ADD_HOLIDAY_PAY_METHOD]: {
          label: 'Add Holiday Pay Method',
          key: 'addHolidayPayMethod',
        },
        [OnboardingStep.ADD_UTR]: { label: 'Add UTR Number', key: 'addUTR' },
        [OnboardingStep.ADD_SIGN_CONTRACT]: {
          label: 'Verify Contract',
          key: 'signContract',
        },
      },
    };
  }

  // Ireland
  //
  // NX-5228: no Working Time Regulation step. The question it asks is Opt-In or
  // Opt-Out of the 48-hour average week, which is a derogation in the UK's
  // Working Time Regulations 1998. Ireland's Organisation of Working Time Act
  // 1997 never adopted the individual opt-out, so neither answer is meaningful
  // and the employee record no longer carries the field on an Irish tenant.
  // The 48-hour WTR *report* is unaffected and still runs for Ireland.
  if (isIreland) {
    return {
      nationalities: [
        'Austrian',
        'Belgium',
        'Bulgarian',
        'Croatian',
        'Citizen of the Dominican Republic',
        'Czech',
        'Denmark',
        'Estonia',
        'Finland',
        'France',
        'Germany',
        'Greece',
        'Hungary',
        'Ireland',
        'Italy',
        'Latvia',
        'Lithuania',
        'Luxembourg',
        'Malta',
        'Netherlands',
        'Poland',
        'Portugal',
        'Romania',
        'Slovakia',
        'Slovenia',
        'Spain and Sweden',
        'Irish',
        'Australian',
        'Danish',
        'Polish',
        'Latvian',
        'Luxembourger',
        'Lithuanian',
        'Portuguese',
        'Romanian',
        'Spanish',
        'Swedish',
        'Hungarian',
        'Italian',
        'Slovak',
        'Slovenian',
        'Cypriot',
        'Estonian',
        'German',
        'Greek',
        'Maltese',
      ],
      stepsTemp: {
        [OnboardingStep.ADD_NATIONALITY]: {
          label: 'Add Nationality',
          key: 'addNationality',
        },
        [OnboardingStep.ADD_ID]: {
          label: 'Verify your ID with passport',
          key: 'addID',
        },
        [OnboardingStep.ADD_SHARE_CODE]: {
          label: 'Add Share Code / EC Check Code',
          key: 'addShareCode',
        },
        [OnboardingStep.ADD_CAPTURE_ID_DOCS]: {
          label: 'Capture ID Docs',
          key: 'addCaptureIdDocs',
        },
        [OnboardingStep.ADD_DOB]: { label: 'Add DOB', key: 'addDOB' },
        [OnboardingStep.ADD_BANK_DETAILS]: {
          label: 'Add Bank Details',
          key: 'addBankDetails',
        },
        [OnboardingStep.ADD_ADDRESS_DETAILS]: {
          label: 'Add Address Details',
          key: 'addAddressDetails',
        },
        [OnboardingStep.ADD_JOB_DESCRIPTION]: {
          label: 'Add Job Title',
          key: 'addJobDescription',
        },
        [OnboardingStep.ADD_NI_NUMBER]: {
          label: 'Add PPS Number',
          key: 'addNINumber',
        },
        [OnboardingStep.ADD_HOLIDAY_PAY_METHOD]: {
          label: 'Add Holiday Pay Method',
          key: 'addHolidayPayMethod',
        },
        [OnboardingStep.ADD_SIGN_CONTRACT]: {
          label: 'Verify Contract',
          key: 'signContract',
        },
      },
      stepsTemp1: {
        [OnboardingStep.ADD_NATIONALITY]: {
          label: 'Add Nationality',
          key: 'addNationality',
        },
        [OnboardingStep.ADD_CAPTURE_ID_DOCS]: {
          label: 'Capture ID Docs',
          key: 'addCaptureIdDocs',
        },
        [OnboardingStep.ADD_SHARE_CODE]: {
          label: 'Add Share Code / EC Check Code',
          key: 'addShareCode',
        },
        [OnboardingStep.ADD_DOB]: { label: 'Add DOB', key: 'addDOB' },
        [OnboardingStep.ADD_BANK_DETAILS]: {
          label: 'Add Bank Details',
          key: 'addBankDetails',
        },
        [OnboardingStep.ADD_ADDRESS_DETAILS]: {
          label: 'Add Address Details',
          key: 'addAddressDetails',
        },
        [OnboardingStep.ADD_JOB_DESCRIPTION]: {
          label: 'Add Job Title',
          key: 'addJobDescription',
        },
        [OnboardingStep.ADD_NI_NUMBER]: {
          label: 'Add PPS Number',
          key: 'addNINumber',
        },
        [OnboardingStep.ADD_HOLIDAY_PAY_METHOD]: {
          label: 'Add Holiday Pay Method',
          key: 'addHolidayPayMethod',
        },
        [OnboardingStep.ADD_SIGN_CONTRACT]: {
          label: 'Verify Contract',
          key: 'signContract',
        },
      },
      stepsTemp2: {
        [OnboardingStep.ADD_NATIONALITY]: {
          label: 'Add Nationality',
          key: 'addNationality',
        },
        [OnboardingStep.ADD_ID]: {
          label: 'Verify your ID with passport',
          key: 'addID',
        },
        [OnboardingStep.ADD_CAPTURE_ID_DOCS]: {
          label: 'Capture ID Docs',
          key: 'addCaptureIdDocs',
        },
        [OnboardingStep.ADD_DOB]: { label: 'Add DOB', key: 'addDOB' },
        [OnboardingStep.ADD_BANK_DETAILS]: {
          label: 'Add Bank Details',
          key: 'addBankDetails',
        },
        [OnboardingStep.ADD_ADDRESS_DETAILS]: {
          label: 'Add Address Details',
          key: 'addAddressDetails',
        },
        [OnboardingStep.ADD_JOB_DESCRIPTION]: {
          label: 'Add Job Title',
          key: 'addJobDescription',
        },
        [OnboardingStep.ADD_NI_NUMBER]: {
          label: 'Add PPS Number',
          key: 'addNINumber',
        },
        [OnboardingStep.ADD_HOLIDAY_PAY_METHOD]: {
          label: 'Add Holiday Pay Method',
          key: 'addHolidayPayMethod',
        },
        [OnboardingStep.ADD_SIGN_CONTRACT]: {
          label: 'Verify Contract',
          key: 'signContract',
        },
      },
    };
  }

  // UK
  return {
    nationalities: UK_RIGHT_TO_WORK_NATIONALITIES,
    stepsTemp: {
      [OnboardingStep.ADD_NATIONALITY]: {
        label: 'Add Nationality',
        key: 'addNationality',
      },
      [OnboardingStep.ADD_ID]: {
        label: 'Verify your ID with passport',
        key: 'addID',
      },
      [OnboardingStep.ADD_SHARE_CODE]: {
        label: 'Add Share Code / EC Check Code',
        key: 'addShareCode',
      },
      [OnboardingStep.ADD_DOB]: { label: 'Add DOB', key: 'addDOB' },
      [OnboardingStep.ADD_BANK_DETAILS]: {
        label: 'Add Bank Details',
        key: 'addBankDetails',
      },
      [OnboardingStep.ADD_ADDRESS_DETAILS]: {
        label: 'Add Address Details',
        key: 'addAddressDetails',
      },
      [OnboardingStep.ADD_JOB_DESCRIPTION]: {
        label: 'Add Job Title',
        key: 'addJobDescription',
      },
      [OnboardingStep.ADD_WORKING_TIME_REGULATION]: {
        label: 'Add Working Time Regulation',
        key: 'addWorkingTimeRegulation',
      },
      [OnboardingStep.ADD_P45_DETAILS]: {
        label: 'Add P45 Details',
        key: 'addP45Details',
      },
      [OnboardingStep.ADD_P45_STARTER_DECLARATION]: {
        label: 'Add P45 Starter Declaration',
        key: 'addP45StarterDeclaration',
      },
      [OnboardingStep.ADD_NI_NUMBER]: {
        label: 'Add NI Number',
        key: 'addNINumber',
      },
      [OnboardingStep.ADD_HOLIDAY_PAY_METHOD]: {
        label: 'Add Holiday Pay Method',
        key: 'addHolidayPayMethod',
      },
      [OnboardingStep.ADD_SIGN_CONTRACT]: {
        label: 'Verify Contract',
        key: 'signContract',
      },
    },
    stepsTemp1: {
      [OnboardingStep.ADD_NATIONALITY]: {
        label: 'Add Nationality',
        key: 'addNationality',
      },
      [OnboardingStep.ADD_SHARE_CODE]: {
        label: 'Add Share Code / EC Check Code',
        key: 'addShareCode',
      },
      [OnboardingStep.ADD_DOB]: { label: 'Add DOB', key: 'addDOB' },
      [OnboardingStep.ADD_BANK_DETAILS]: {
        label: 'Add Bank Details',
        key: 'addBankDetails',
      },
      [OnboardingStep.ADD_ADDRESS_DETAILS]: {
        label: 'Add Address Details',
        key: 'addAddressDetails',
      },
      [OnboardingStep.ADD_JOB_DESCRIPTION]: {
        label: 'Add Job Title',
        key: 'addJobDescription',
      },
      [OnboardingStep.ADD_WORKING_TIME_REGULATION]: {
        label: 'Add Working Time Regulation',
        key: 'addWorkingTimeRegulation',
      },
      [OnboardingStep.ADD_P45_DETAILS]: {
        label: 'Add P45 Details',
        key: 'addP45Details',
      },
      [OnboardingStep.ADD_P45_STARTER_DECLARATION]: {
        label: 'Add P45 Starter Declaration',
        key: 'addP45StarterDeclaration',
      },
      [OnboardingStep.ADD_NI_NUMBER]: {
        label: 'Add NI Number',
        key: 'addNINumber',
      },
      [OnboardingStep.ADD_HOLIDAY_PAY_METHOD]: {
        label: 'Add Holiday Pay Method',
        key: 'addHolidayPayMethod',
      },
      [OnboardingStep.ADD_SIGN_CONTRACT]: {
        label: 'Verify Contract',
        key: 'signContract',
      },
    },
    stepsTemp2: {
      [OnboardingStep.ADD_NATIONALITY]: {
        label: 'Add Nationality',
        key: 'addNationality',
      },
      [OnboardingStep.ADD_ID]: {
        label: 'Verify your ID with passport',
        key: 'addID',
      },
      [OnboardingStep.ADD_DOB]: { label: 'Add DOB', key: 'addDOB' },
      [OnboardingStep.ADD_BANK_DETAILS]: {
        label: 'Add Bank Details',
        key: 'addBankDetails',
      },
      [OnboardingStep.ADD_ADDRESS_DETAILS]: {
        label: 'Add Address Details',
        key: 'addAddressDetails',
      },
      [OnboardingStep.ADD_JOB_DESCRIPTION]: {
        label: 'Add Job Title',
        key: 'addJobDescription',
      },
      [OnboardingStep.ADD_WORKING_TIME_REGULATION]: {
        label: 'Add Working Time Regulation',
        key: 'addWorkingTimeRegulation',
      },
      [OnboardingStep.ADD_P45_DETAILS]: {
        label: 'Add P45 Details',
        key: 'addP45Details',
      },
      [OnboardingStep.ADD_P45_STARTER_DECLARATION]: {
        label: 'Add P45 Starter Declaration',
        key: 'addP45StarterDeclaration',
      },
      [OnboardingStep.ADD_NI_NUMBER]: {
        label: 'Add NI Number',
        key: 'addNINumber',
      },
      [OnboardingStep.ADD_HOLIDAY_PAY_METHOD]: {
        label: 'Add Holiday Pay Method',
        key: 'addHolidayPayMethod',
      },
      [OnboardingStep.ADD_SIGN_CONTRACT]: {
        label: 'Verify Contract',
        key: 'signContract',
      },
    },
  };
};

export default getOnboardingStepAndNationalities;
